import React, { useState, useEffect, useRef } from 'react';
import styles from './downloadApp.module.css';

interface DownloadAppProps {
  redirectUrl?: string;
  countdownSeconds?: number;
  showButton?: boolean;
  showRedirecting?: boolean;
  onClick?: () => void;
  onRedirect?: (context: 'auto' | 'button') => void;
}

// Flag ONLY for auto-redirect countdown (not for button clicks)
let autoRedirectDone = false;

const DownloadApp: React.FC<DownloadAppProps> = ({ 
  redirectUrl = 'https://play.google.com/store/apps/details?id=com.eight.network.android.vertical',
  countdownSeconds = 5,
  showButton = true,
  showRedirecting = true,
  onClick,
  onRedirect
}) => {
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [progress, setProgress] = useState(0);
  const autoRedirectRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const redirectUrlRef = useRef(redirectUrl);

  // Keep URL ref in sync with prop
  useEffect(() => {
    redirectUrlRef.current = redirectUrl;
    console.log('[DownloadApp] URL updated:', redirectUrl);
  }, [redirectUrl]);

  // Get final redirect URL
  const getRedirectLink = (): string => {
    const targetUrl = redirectUrlRef.current;
    
    // Deep link or AppsFlyer OneLink - use as-is
    if (targetUrl.includes('verticaltvbyzupee.com') || targetUrl.includes('.onelink.me')) {
      return targetUrl;
    }
    
    // Play Store URL - add source tracking
    const separator = targetUrl.includes('?') ? '&' : '?';
    const finalUrl = `${targetUrl}${separator}source=trailpwawebsite`;
    return finalUrl;
  };

  // AUTO-REDIRECT COUNTDOWN (only for showRedirecting=true component)
  useEffect(() => {
    if (!showRedirecting) {
      return; // Skip for button-only component
    }

    // Prevent multiple auto-redirects
    if (autoRedirectDone || autoRedirectRef.current) {
      return;
    }

    console.log('[DownloadApp] Starting countdown. URL:', redirectUrlRef.current);

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          
          // Auto-redirect only if not already done
          if (!autoRedirectDone && !autoRedirectRef.current) {
            autoRedirectDone = true;
            autoRedirectRef.current = true;
            
            if (onRedirect) {
              onRedirect('auto');
            } else {
              const url = getRedirectLink();
              console.log('[DownloadApp] Auto-redirect executing:', url);
              window.location.href = url;
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
          }
          return 100;
        }
        return prev + (100 / (countdownSeconds * 10));
      });
    }, 100);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [showRedirecting, countdownSeconds]);

  // BUTTON CLICK - Always works, independent of auto-redirect
  const handleDownloadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Stop any ongoing countdown
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    autoRedirectDone = true;
    autoRedirectRef.current = true;
    
    // Call tracking callback (errors won't block navigation)
    if (onClick) {
      try {
        onClick();
      } catch (err) {
        console.error('[DownloadApp] Tracking error:', err);
      }
    }
    
    if (onRedirect) {
      onRedirect('button');
      return;
    }

    // Get URL first (before any potential errors in tracking)
    const url = getRedirectLink();
    console.log('[DownloadApp] BUTTON CLICKED! Navigating to:', url);
    
    // NAVIGATE - This is the critical line
    window.location.href = url;
  };

  return (
    <>
      {showButton && (
        <button 
          type="button"
          className={styles.downloadButton}
          onClick={handleDownloadClick}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={styles.downloadIcon}
          >
            <path 
              d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2v9.67z" 
              fill="currentColor"
            />
          </svg>
          <span className={styles.downloadButtonText}>Download App</span>
        </button>
      )}
      
      {showRedirecting && (
        <div className={styles.redirectingContainer}>
          <p className={styles.redirectingText}>redirecting in {countdown}</p>
          <div className={styles.progressBarContainer}>
            <div 
              className={styles.progressBar}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadApp;
